import { Component, DestroyRef, Inject, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { LDF_DISABLE_OKTA_MIGRATION_POPUP_DISMISSAL, validatorPatterns } from '@app/constants';
import { IOktaEmailOption, UserService } from '@data/services/user/user.service';
import { LaunchdarklyService } from '@shared/services/LaunchDarkly/launchdarkly.service';
import { ToastMsgService } from '@shared/services/toastr/toast-msg.service';
import { finalize, first } from 'rxjs';

const OTHER_EMAIL_OPTION = '__other__';

interface IOktaEmailSelectionDialogData {
  userId: string;
  defaultEmail: string;
}

@Component({
  standalone: false,
  selector: 'app-okta-email-selection',
  templateUrl: './okta-email-selection.component.html',
  styleUrls: ['./okta-email-selection.component.scss']
})
export class OktaEmailSelectionComponent implements OnInit {
  private readonly _destroyRef = inject(DestroyRef);

  public isLoading: boolean = false;
  public isSubmitting: boolean = false;
  public selectedEmail: string = '';
  public otherEmail: string = '';
  public emailOptions: IOktaEmailOption[] = [];
  public showInvalidEmailError: boolean = false;
  public showUnavailableEmailError: boolean = false;
  public enteredEmail: string = '';
  public errorMessage: string = '';
  public submitDisabled: boolean = false;
  public disableOktaMigrationPopupDismissal: boolean = false;

  public get hasSelection(): boolean {
    return !!this.selectedEmail;
  }

  public get isOtherSelected(): boolean {
    return this.selectedEmail === OTHER_EMAIL_OPTION;
  }

  constructor(
    private _userService: UserService,
    private _toastMsgService: ToastMsgService,
    private _launchDarklySvc: LaunchdarklyService,
    private _dialogRef: MatDialogRef<OktaEmailSelectionComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IOktaEmailSelectionDialogData
  ) {}

  public ngOnInit(): void {
    this._loadUserEmails();
    this._watchPopupDismissalFlag();
  }

  public closeDialog(): void {
    this._dialogRef.close(false);
  }

  public submit(): void {
    this._clearErrors();

    const emailToUse = this._getSelectedEmail();
    if (!emailToUse) {
      return;
    }

    if (this.isOtherSelected && !validatorPatterns.EMAIL.test(emailToUse)) {
      this.showInvalidEmailError = true;
      return;
    }

    this.isSubmitting = true;

    this._userService.isOktaEmailAvailable(this.data.userId, emailToUse)
      .pipe(first())
      .subscribe({
        next: (isAvailable) => {
          if (!isAvailable) {
            this.showUnavailableEmailError = true;
            this.isSubmitting = false;
            return;
          }

          this._saveOktaEmail(emailToUse);
        },
        error: () => {
          this.isSubmitting = false;
        }
      });
  }

  private _saveOktaEmail(emailToUse: string): void {
    this._userService.createOktaCredential(this.data.userId, emailToUse)
      .pipe(first())
      .subscribe({
        next: () => {
          this._toastMsgService.showSuccess('Success -- New Okta Username Created! (Continue logging in as you do today until further notice.)');
          this._dialogRef.close(true);
        },
        error: () => {
          this.showUnavailableEmailError = true;
          this.isSubmitting = false;
        },
        complete: () => {
          this.isSubmitting = false;
        }
      });
  }

  private _loadUserEmails(): void {
    this.isLoading = true;

    this._userService.searchUserEmails(this.data.userId, this.data.defaultEmail)
      .pipe(
        first(),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe({
        next: (emailOptions) => {
          this.emailOptions = emailOptions;
        },
        error: () => {
          this.emailOptions = [];
        }
      });
  }

  private _getSelectedEmail(): string {
    if (!this.isOtherSelected) {
      return (this.selectedEmail || '').trim().toLowerCase();
    }

    return (this.otherEmail || '').trim().toLowerCase();
  }

  private _clearErrors(): void {
    this.showInvalidEmailError = false;
    this.showUnavailableEmailError = false;
  }

  private _watchPopupDismissalFlag(): void {
    this._launchDarklySvc.getFlag(LDF_DISABLE_OKTA_MIGRATION_POPUP_DISMISSAL)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((flag) => {
        this.disableOktaMigrationPopupDismissal = !!flag;
        this._dialogRef.disableClose = this.disableOktaMigrationPopupDismissal;
      });
  }

  public onRadioChange(selected: string): void {
    this.selectedEmail = selected;
    this._clearErrors();
    this.submitDisabled = false;  
    //this.submitDisabled = this.isOtherSelected && !this.isEmailValid(this.otherEmail);
  }

  public onEmailInputChange(): void {
    this._clearErrors();
    console.log('Email input changed:', this.otherEmail);
    console.log(this.isOtherSelected, this.isEmailValid(this.otherEmail));
    this.submitDisabled = false;
  }

  public isEmailValid(email: string): boolean {
    return validatorPatterns.EMAIL.test(email.trim().toLowerCase());
  }
} 


